import React from "react";
import { Button, List } from "antd";
import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import { LuChevronRight } from "react-icons/lu";

const TYPE_AVATAR = {
  SEND: {
    bg: "#dbeafe",
    color: "#1d4ed8",
    icon: <ArrowUpOutlined className="dashboardTxnAvatarIconSend" />,
  },
  RECEIVE: {
    bg: "#ede9fe",
    color: "#6d28d9",
    icon: <ArrowDownOutlined className="dashboardTxnAvatarIconReceive" />,
  },
  ADD_FUND: { bg: "#dcfce7", color: "#15803d", icon: <PlusOutlined /> },
  WITHDRAW: { bg: "#ffedd5", color: "#c2410c", icon: <ArrowUpOutlined /> },
};

const formatStatusLabel = (status) => {
  if (!status) return "Recorded";
  return String(status)
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const isPendingStatus = (status) =>
  ["PENDING", "PROCESSING", "IN_PROGRESS", "SUBMITTED"].includes(
    String(status || "").toUpperCase(),
  );

const isRejectedStatus = (status) =>
  ["REJECTED", "FAILED", "CANCELLED", "DECLINED"].includes(
    String(status || "").toUpperCase(),
  );

const formatAmt = (amt, curr) =>
  amt != null
    ? `${Number(amt).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${curr || ""}`.trim()
    : "";

const mapToActivity = (item) => {
  const isDebit = item.direction === "DEBIT";
  const recipientName = `${item.recipientFirstName || ""} ${item.recipientLastName || ""}`.trim();
  let title;
  let detailText = null;
  let secondaryAmountText = null;
  const paymentMethod = String(item.paymentMethod || "").trim();

  if (item.type === "REMITTANCE") {
    title = recipientName ? `Send to ${recipientName}` : "Send";
    detailText = null;
    if (item.receivedAmount != null && item.targetCurrency) {
      secondaryAmountText = `Recipient gets ${formatAmt(
        item.receivedAmount,
        item.targetCurrency,
      )}`;
    }
  } else if (item.type === "WALLET_TRANSFER") {
    title = isDebit
      ? recipientName
        ? `Send to ${recipientName}`
        : "Send"
      : recipientName
        ? `Receive from ${recipientName}`
        : "Receive";
    detailText = null;
  } else if (item.type === "ADD_FUND") {
    title = paymentMethod
      ? `Add Funds via ${paymentMethod}`
      : "Add Funds";
    detailText = null;
  } else {
    title = "Withdraw";
    detailText = item.reason || null;
  }

  return {
    type:
      item.type === "ADD_FUND"
        ? "ADD_FUND"
        : item.type === "ADJUSTMENT"
          ? "WITHDRAW"
          : isDebit
            ? "SEND"
            : "RECEIVE",
    direction: isDebit ? "out" : "in",
    title,
    detailText,
    amountText: formatAmt(item.amount, item.currency),
    secondaryAmountText,
    status: item.status || "",
    isPending: isPendingStatus(item.status),
    isRejected: isRejectedStatus(item.status),
    statusLabel: formatStatusLabel(item.status),
    isRemittance: item.type === "REMITTANCE",
    referenceID: item.referenceID || "",
    id: item.id || "",
    raw: item,
    timestamp: item.date,
  };
};

const RecentTransactions = ({
  activityHistory = [],
  onViewAll,
  onOpenTransactionDetails,
  formatDateToLocal,
}) => {
  const items = activityHistory.slice(0, 5).map(mapToActivity);

  return (
    <div className="appSectionBlock">
      <div className="appSectionHeader">
        <h2 className="appSectionTitle">Recent Transactions</h2>
        <Button className="appLinkBtn" type="text" onClick={onViewAll}>
          View All
        </Button>
      </div>
      <List
        itemLayout="horizontal"
        dataSource={items}
        className="appListCard dashboardTxnList"
        locale={{ emptyText: "No activity yet" }}
        renderItem={(item) => (
          <List.Item
            className="dashboardTxnItem dashboardTxnItemClickable"
            onClick={() => onOpenTransactionDetails?.(item)}
          >
            <div className="dashboardTxnMain">
              <div
                className="dashboardTxnAvatar"
                style={{
                  background: TYPE_AVATAR[item.type]?.bg || "#f0f0f0",
                  color: TYPE_AVATAR[item.type]?.color || "#555",
                }}
              >
                {TYPE_AVATAR[item.type]?.icon || "?"}
              </div>
              <div className="dashboardTxnBody">
                <div className="dashboardTxnTopLine">
                  <span className="dashboardTxnName">{item.title}</span>
                </div>
                <div className="dashboardTxnMetaLine">
                  <span className="dashboardTxnMeta">
                    {formatDateToLocal(item.timestamp)}
                  </span>
                </div>
              </div>
              <div className="dashboardTxnAmountBlock">
                <div className="dashboardTxnAmountContent">
                  <strong
                    className={`dashboardTxnAmount ${
                      item.isPending
                        ? "dashboardTxnPendingAmount"
                        : item.isRejected
                          ? "dashboardTxnRejectedAmount"
                        : item.direction === "out"
                          ? "dashboardTxnDebit"
                          : "dashboardTxnCredit"
                    }`}
                  >
                    {item.direction === "out" ? "−" : "+"}
                    {item.amountText}
                  </strong>
                  {item.secondaryAmountText ? (
                    <span className="dashboardTxnSecondaryAmount">
                      {item.secondaryAmountText}
                    </span>
                  ) : null}
                  {item.isPending ? (
                    <span className="dashboardTxnAmountState dashboardTxnAmountStatePending">
                      <ClockCircleOutlined className="dashboardTxnPendingIcon" />
                      Pending
                    </span>
                  ) : null}
                  {item.isRejected ? (
                    <span className="dashboardTxnAmountState dashboardTxnAmountStateRejected">
                      <CloseCircleOutlined className="dashboardTxnRejectedIcon" />
                      {item.statusLabel}
                    </span>
                  ) : null}
                </div>
                <Button
                  type="text"
                  className="dashboardTxnDetailBtn"
                  icon={<LuChevronRight />}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenTransactionDetails?.(item);
                  }}
                  aria-label="View transaction details"
                />
              </div>
            </div>
          </List.Item>
        )}
      />
    </div>
  );
};

export default RecentTransactions;
